import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["circle"]
  static classes = ["introPending", "ready", "hinting"]
  static values = {
    introStagger: { type: Number, default: 150 },
    hintDelay: { type: Number, default: 900 },
    discSelector: { type: String, default: ".landing-venn-circle__disc" },
  }

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.dismissed = false
    this.timers = []

    this.circleTargets.forEach((circle) => {
      circle.addEventListener("pointerenter", this.dismiss)
      circle.addEventListener("focus", this.dismiss)
    })

    if (this.reducedMotion) {
      this.element.classList.remove(this.introPendingClass)
      this.circleTargets.forEach((circle) => circle.classList.add(this.readyClass))
      return
    }

    this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.4 })
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.clearTimers()
    this.circleTargets.forEach((circle) => {
      circle.removeEventListener("pointerenter", this.dismiss)
      circle.removeEventListener("focus", this.dismiss)
      this.disc(circle)?.removeEventListener("animationend", this.endHint)
    })
  }

  onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return
    this.observer.disconnect()
    this.playIntro()
  }

  playIntro() {
    this.circleTargets.forEach((circle, index) => {
      this.defer(() => circle.classList.add(this.readyClass), index * this.introStaggerValue)
    })

    const introDone = (this.circleTargets.length - 1) * this.introStaggerValue + this.hintDelayValue
    this.defer(() => this.playHint(), introDone)
  }

  playHint() {
    if (this.dismissed) return

    const circle = this.circleTargets[0]
    if (!circle) return

    circle.classList.add(this.hintingClass)
    this.disc(circle)?.addEventListener("animationend", this.endHint, { once: true })
  }

  endHint = () => {
    this.circleTargets[0]?.classList.remove(this.hintingClass)
  }

  dismiss = () => {
    if (this.dismissed) return
    this.dismissed = true
    this.clearTimers()
    this.circleTargets.forEach((circle) => {
      circle.classList.remove(this.hintingClass)
      circle.classList.add(this.readyClass)
      this.disc(circle)?.removeEventListener("animationend", this.endHint)
    })
  }

  disc(circle) {
    return circle.querySelector(this.discSelectorValue)
  }

  defer(callback, delay) {
    this.timers.push(setTimeout(callback, delay))
  }

  clearTimers() {
    this.timers.forEach(clearTimeout)
    this.timers = []
  }
}
